/**
 * Admin Console — teljesen elkülönített platform admin felület
 * Saját auth (adminToken localStorage), saját API helper, nem függ az AuthProvider-től.
 * Route: /admin
 */
import { useState, useEffect, useCallback } from 'react';

// ── Admin API helper ──────────────────────────────────────────────────────────

const ADMIN_BASE = '/api/admin';

async function adminFetch<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = localStorage.getItem('adminToken');
  const res = await fetch(ADMIN_BASE + path, {
    method: opts.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401 && token) {
    // Session lejárt (volt token) — kijelentkeztetés
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.reload();
    throw new Error('Munkamenet lejárt');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).error || 'Ismeretlen hiba');
  return data as T;
}

const aGet = <T,>(p: string) => adminFetch<T>(p);
const aPost = <T,>(p: string, b: unknown) => adminFetch<T>(p, { method: 'POST', body: b });
const aPatch = <T,>(p: string, b: unknown) => adminFetch<T>(p, { method: 'PATCH', body: b });
const aDel = <T,>(p: string, b?: unknown) => adminFetch<T>(p, { method: 'DELETE', body: b });

// ── Típusok ──────────────────────────────────────────────────────────────────

interface AdminUser { id: string; email: string; name: string; role: string; must_change_password: boolean; totp_enabled: boolean; }
interface TenantRow { id: string; name: string; plan_code: string; billing_cycle: string; sub_status: string; trial_ends_at: string | null; current_period_ends_at: string | null; user_count: number; is_suspended: boolean; }
interface Metrics { mrr_huf: number; arr_huf: number; arpu_huf: number; churn_rate: number; trial_conv_rate: number; active_tenants: number; trial_tenants: number; }
interface AdminRow { id: string; email: string; name: string; role: string; is_active: boolean; last_login_at: string | null; created_at: string; }
interface BroadcastRow { id: string; title: string; body: string; channel: string; status: string; created_at: string; sent_at: string | null; }
interface LeadRow { id: string; email: string; name: string | null; company_name: string | null; status: string; source: string | null; created_at: string; }
interface ApprovalRow { id: string; action_type: string; description: string; status: string; requester_email: string; created_at: string; }
interface CouponRow { id: string; code: string; description: string | null; discount_type: string; discount_value: number; is_active: boolean; used_count: number; max_uses: number | null; }

type Tab = 'dashboard' | 'tenants' | 'users' | 'admins' | 'finance' | 'coupons' | 'broadcasts' | 'leads' | 'approvals' | 'health' | 'audit';

// ── Stílusok ─────────────────────────────────────────────────────────────────

const S = {
  wrap: { minHeight: '100vh', display: 'flex', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, background: '#f5f5f7', color: '#1a1a1a' } as React.CSSProperties,
  sidebar: { width: 220, background: '#1e2232', display: 'flex', flexDirection: 'column', flexShrink: 0 } as React.CSSProperties,
  sidebarLogo: { padding: '20px 18px 14px', color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: '-0.5px', borderBottom: '1px solid rgba(255,255,255,.08)' } as React.CSSProperties,
  sidebarSub: { fontSize: 10, fontWeight: 400, color: 'rgba(255,255,255,.45)', display: 'block', marginTop: 2 } as React.CSSProperties,
  sidebarNav: { flex: 1, padding: '10px 0', overflowY: 'auto' as const },
  sidebarItem: (active: boolean): React.CSSProperties => ({
    display: 'block', padding: '9px 18px', cursor: 'pointer', borderRadius: 0,
    background: active ? 'rgba(255,255,255,.12)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,.6)',
    fontWeight: active ? 600 : 400,
    fontSize: 13, border: 'none', width: '100%', textAlign: 'left',
    borderLeft: active ? '3px solid #6366f1' : '3px solid transparent',
    transition: 'all .15s',
  }),
  sidebarSection: { padding: '12px 18px 4px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.3)', textTransform: 'uppercase' as const, letterSpacing: '0.08em' },
  sidebarUser: { padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,.08)', fontSize: 12, color: 'rgba(255,255,255,.5)' },
  main: { flex: 1, padding: '28px 32px', overflowY: 'auto' as const, maxWidth: '100%' },
  pageTitle: { fontSize: 22, fontWeight: 800, marginBottom: 6, color: '#0f172a' } as React.CSSProperties,
  pageSub: { fontSize: 13, color: '#64748b', marginBottom: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid #e9ecef' } as React.CSSProperties,
  btn: (variant: 'primary' | 'secondary' | 'danger' | 'ghost'): React.CSSProperties => ({
    padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
    background: variant === 'primary' ? '#6366f1' : variant === 'danger' ? '#ef4444' : variant === 'secondary' ? '#e2e8f0' : 'transparent',
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? '#fff' : '#374151',
    transition: 'opacity .15s',
  }),
  input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' as const, outline: 'none' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: '#374151' } as React.CSSProperties,
  th: { textAlign: 'left' as const, padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, background: '#f8fafc', borderBottom: '2px solid #e9ecef' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f1f5f9' } as React.CSSProperties,
  badge: (color: 'green' | 'red' | 'yellow' | 'blue' | 'gray'): React.CSSProperties => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
    background: color === 'green' ? '#dcfce7' : color === 'red' ? '#fee2e2' : color === 'yellow' ? '#fef9c3' : color === 'blue' ? '#dbeafe' : '#f1f5f9',
    color: color === 'green' ? '#166534' : color === 'red' ? '#991b1b' : color === 'yellow' ? '#854d0e' : color === 'blue' ? '#1e40af' : '#64748b',
  }),
  modal: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBox: { background: '#fff', borderRadius: 14, padding: '28px 28px 24px', width: 420, maxHeight: '90vh', overflowY: 'auto' as const, boxShadow: '0 20px 60px rgba(0,0,0,.3)' },
  err: { color: '#ef4444', fontSize: 12, marginTop: 4 } as React.CSSProperties,
  ok: { color: '#16a34a', fontSize: 12, marginTop: 4 } as React.CSSProperties,
};

function fmtDate(d: string | null) { return d ? new Date(d).toLocaleDateString('hu-HU') : '—'; }
function fmtDateTime(d: string | null) { return d ? new Date(d).toLocaleString('hu-HU') : '—'; }
function fmtHuf(v: number | string) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? '—' : n.toLocaleString('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 });
}
function subBadge(s: string): 'green' | 'red' | 'yellow' | 'blue' | 'gray' {
  if (s === 'active') return 'green';
  if (['cancelled', 'ended', 'locked'].includes(s)) return 'red';
  if (['past_due', 'grace', 'read_only'].includes(s)) return 'yellow';
  if (s === 'trial') return 'blue';
  return 'gray';
}

// ── Login oldal ──────────────────────────────────────────────────────────────

function AdminLoginPage({ onLogin }: { onLogin: (user: AdminUser, token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phase, setPhase] = useState<'credentials' | 'otp' | 'totp'>('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [requireTotp, setRequireTotp] = useState(false);
  const [code, setCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function handleStep1(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const r = await adminFetch<{ requireTotp: boolean; pendingToken: string }>('/auth/login', {
        method: 'POST', body: { email, password },
      });
      setPendingToken(r.pendingToken);
      setRequireTotp(r.requireTotp);
      setPhase('otp');
    } catch (ex: any) { setErr(ex.message); }
    finally { setLoading(false); }
  }

  async function handleStep2(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const body: any = { pendingToken, code };
      if (requireTotp && totpCode) body.totpCode = totpCode;

      const r = await adminFetch<{ requireTotp?: boolean; token?: string; admin?: AdminUser }>('/auth/verify-email-otp', {
        method: 'POST', body,
      });
      if (r.requireTotp && !totpCode) {
        setPhase('totp'); setLoading(false); return;
      }
      if (r.token && r.admin) {
        localStorage.setItem('adminToken', r.token);
        localStorage.setItem('adminUser', JSON.stringify(r.admin));
        onLogin(r.admin, r.token);
      }
    } catch (ex: any) { setErr(ex.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '36px 36px 32px', width: 380, boxShadow: '0 25px 60px rgba(0,0,0,.4)' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Modulum Admin</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Platform konzol — csak operátoroknak</div>
        </div>

        {phase === 'credentials' && (
          <form onSubmit={handleStep1} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>
            <div>
              <label style={S.label}>Jelszó</label>
              <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            {err && <div style={S.err}>{err}</div>}
            <button style={{ ...S.btn('primary'), padding: '10px 14px', fontSize: 14, marginTop: 4 }} disabled={loading}>
              {loading ? 'Bejelentkezés...' : 'Bejelentkezés'}
            </button>
          </form>
        )}

        {phase === 'otp' && (
          <form onSubmit={handleStep2} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#475569', background: '#f1f5f9', borderRadius: 8, padding: '10px 14px' }}>
              6 jegyű kódot küldtünk a <strong>{email}</strong> email-re. Érvényes: 10 percig.
            </div>
            <div>
              <label style={S.label}>Email OTP kód</label>
              <input style={S.input} value={code} onChange={e => setCode(e.target.value)} placeholder="123456" maxLength={6} inputMode="numeric" pattern="[0-9]*" autoFocus />
            </div>
            {requireTotp && (
              <div>
                <label style={S.label}>Authenticator kód (TOTP)</label>
                <input style={S.input} value={totpCode} onChange={e => setTotpCode(e.target.value)} placeholder="123456" maxLength={6} inputMode="numeric" pattern="[0-9]*" />
              </div>
            )}
            {err && <div style={S.err}>{err}</div>}
            <button style={{ ...S.btn('primary'), padding: '10px 14px', fontSize: 14 }} disabled={loading}>
              {loading ? 'Ellenőrzés...' : 'Megerősítés'}
            </button>
            <button type="button" style={{ ...S.btn('ghost'), fontSize: 12, color: '#64748b' }} onClick={() => { setPhase('credentials'); setErr(''); }}>
              Vissza
            </button>
          </form>
        )}

        {phase === 'totp' && (
          <form onSubmit={handleStep2} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#475569', background: '#f1f5f9', borderRadius: 8, padding: '10px 14px' }}>
              Add meg az Authenticator alkalmazásban megjelenő 6 jegyű kódot.
            </div>
            <div>
              <label style={S.label}>TOTP kód</label>
              <input style={S.input} value={totpCode} onChange={e => setTotpCode(e.target.value)} placeholder="123456" maxLength={6} inputMode="numeric" pattern="[0-9]*" autoFocus />
            </div>
            {err && <div style={S.err}>{err}</div>}
            <button style={{ ...S.btn('primary'), padding: '10px 14px', fontSize: 14 }} disabled={loading}>
              {loading ? 'Ellenőrzés...' : 'Bejelentkezés'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Jelszócsere (must_change_password) ───────────────────────────────────────

function ChangePasswordPage({ onDone }: { onDone: () => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (pw !== pw2) { setErr('A két jelszó nem egyezik.'); return; }
    if (pw.length < 12) { setErr('Legalább 12 karakter szükséges.'); return; }
    setLoading(true); setErr('');
    try {
      await aPost('/auth/change-password', { oldPassword: '', newPassword: pw });
      // Update stored user
      const u = JSON.parse(localStorage.getItem('adminUser') || '{}');
      u.must_change_password = false;
      localStorage.setItem('adminUser', JSON.stringify(u));
      onDone();
    } catch (ex: any) { setErr(ex.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '36px', width: 380, boxShadow: '0 25px 60px rgba(0,0,0,.4)' }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Jelszócsere szükséges</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Az első bejelentkezéshez új jelszót kell beállítanod.</div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={S.label}>Új jelszó (min. 12 karakter)</label>
            <input style={S.input} type="password" value={pw} onChange={e => setPw(e.target.value)} required autoFocus />
          </div>
          <div>
            <label style={S.label}>Jelszó megerősítése</label>
            <input style={S.input} type="password" value={pw2} onChange={e => setPw2(e.target.value)} required />
          </div>
          {err && <div style={S.err}>{err}</div>}
          <button style={{ ...S.btn('primary'), padding: '10px 14px' }} disabled={loading}>
            {loading ? 'Mentés...' : 'Jelszó mentése'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard (metrikák) ──────────────────────────────────────────────────────

function DashboardSection() {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [funnel, setFunnel] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      aGet<Metrics>('/finance/metrics').catch(() => null),
      aGet<any>('/finance/funnel').catch(() => null),
      aGet<any>('/system/health').catch(() => null),
    ]).then(([m, f, h]) => { setMetrics(m); setFunnel(f); setHealth(h); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Betöltés...</div>;

  const mCards = metrics ? [
    { label: 'MRR', value: fmtHuf(metrics.mrr_huf), color: '#6366f1' },
    { label: 'ARR', value: fmtHuf(metrics.arr_huf), color: '#0891b2' },
    { label: 'ARPU', value: fmtHuf(metrics.arpu_huf), color: '#0f766e' },
    { label: 'Churn ráta', value: `${(metrics.churn_rate * 100).toFixed(1)}%`, color: '#dc2626' },
    { label: 'Trial konv.', value: `${(metrics.trial_conv_rate * 100).toFixed(1)}%`, color: '#16a34a' },
    { label: 'Aktív tenant', value: metrics.active_tenants, color: '#7c3aed' },
    { label: 'Trial tenant', value: metrics.trial_tenants, color: '#ca8a04' },
  ] : [];

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Dashboard</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Platform áttekintő</div>

      {metrics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 14, marginBottom: 28 }}>
          {mCards.map(c => (
            <div key={c.label} style={S.card}>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {funnel && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>Regisztrációs tölcsér (utolsó 30 nap)</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Regisztrált', value: funnel.registered },
              { label: 'Onboarding', value: funnel.onboarded },
              { label: 'Trial', value: funnel.trialing },
              { label: 'Fizető', value: funnel.paying },
            ].map(f => (
              <div key={f.label} style={{ textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{f.value ?? 0}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {health && (
        <div style={{ ...S.card }}>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>Rendszer állapot</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {Object.entries(health).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: v === 'ok' || v === true ? '#16a34a' : '#ef4444' }} />
                <span style={{ fontWeight: 600 }}>{k}</span>
                <span style={{ color: '#64748b' }}>{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tenant-kezelés ────────────────────────────────────────────────────────────

function TenantSection() {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async (q = search, st = status) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50' });
      if (q) p.set('search', q);
      if (st) p.set('status', st);
      const r = await aGet<{ rows: TenantRow[]; total: number }>(`/tenants?${p}`);
      setTenants(r.rows); setTotal(r.total);
    } catch { }
    finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  async function handleSuspend(t: TenantRow) {
    const reason = prompt('Felfüggesztés oka (min 5 karakter):');
    if (!reason || reason.length < 5) return;
    try { await aPost(`/tenants/${t.id}/suspend`, { reason }); setMsg('Felfüggesztve.'); load(); }
    catch (e: any) { setMsg(e.message); }
  }
  async function handleUnsuspend(t: TenantRow) {
    try { await aPost(`/tenants/${t.id}/unsuspend`, {}); setMsg('Aktiválva.'); load(); }
    catch (e: any) { setMsg(e.message); }
  }

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Tenantok</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Előfizetés-kezelés, felfüggesztés, feature flag-ek</div>

      {msg && <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, color: '#166534', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, width: 260 }} placeholder="Keresés neve alapján..." value={search}
          onChange={e => { setSearch(e.target.value); load(e.target.value, status); }} />
        <select value={status} onChange={e => { setStatus(e.target.value); load(search, e.target.value); }}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">Minden állapot</option>
          {['trial','active','past_due','grace','read_only','locked','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b', alignSelf: 'center' }}>{total} tenant</span>
      </div>

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Tenant', 'Csomag', 'Állapot', 'Trial vége', 'Felhasználók', 'Műveletek'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : tenants.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>Nincs találat.</td></tr>
            ) : tenants.map(t => (
              <tr key={t.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{t.name}{t.is_suspended && <span style={{ ...S.badge('red'), marginLeft: 6 }}>Felfügg.</span>}</td>
                <td style={S.td}>{(t.plan_code || '—').toUpperCase()} / {t.billing_cycle === 'yearly' ? 'éves' : 'havi'}</td>
                <td style={S.td}><span style={S.badge(subBadge(t.sub_status))}>{t.sub_status || '—'}</span></td>
                <td style={S.td}>{fmtDate(t.trial_ends_at)}</td>
                <td style={{ ...S.td, textAlign: 'center' }}>{t.user_count}</td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={S.btn('secondary')} onClick={() => setSelected(t)}>Szerkesztés</button>
                    {t.is_suspended
                      ? <button style={S.btn('ghost')} onClick={() => handleUnsuspend(t)}>Aktiválás</button>
                      : <button style={S.btn('ghost')} onClick={() => handleSuspend(t)}>Felfüggesztés</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <TenantDetailModal tenant={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function TenantDetailModal({ tenant, onClose, onSaved }: { tenant: TenantRow; onClose: () => void; onSaved: () => void }) {
  const [plan, setPlan] = useState(tenant.plan_code || 'start');
  const [cycle, setCycle] = useState(tenant.billing_cycle || 'monthly');
  const [subStatus, setSubStatus] = useState(tenant.sub_status || 'active');
  const [trialEnd, setTrialEnd] = useState(tenant.trial_ends_at?.slice(0, 10) || '');
  const [feature, setFeature] = useState('');
  const [featureOn, setFeatureOn] = useState(true);
  const [notes, setNotes] = useState<any[]>([]);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'sub' | 'flags' | 'notes'>('sub');

  useEffect(() => {
    aGet<any[]>(`/tenants/${tenant.id}/notes`).then(setNotes).catch(() => {});
  }, [tenant.id]);

  async function handleSaveSub() {
    setSaving(true); setErr('');
    try {
      await aPatch(`/tenants/${tenant.id}/subscription`, {
        planCode: plan, billingCycle: cycle, status: subStatus,
        ...(trialEnd ? { trialEndsAt: trialEnd } : {}),
      });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function handleSetFlag() {
    if (!feature) return;
    setSaving(true); setErr('');
    try {
      await aPost(`/tenants/${tenant.id}/feature-flag`, { feature, enabled: featureOn });
      setErr(''); alert('Feature flag beállítva.');
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function handleAddNote() {
    if (!noteText.trim()) return;
    try {
      await aPost(`/tenants/${tenant.id}/notes`, { content: noteText.trim() });
      const updated = await aGet<any[]>(`/tenants/${tenant.id}/notes`);
      setNotes(updated); setNoteText('');
    } catch (e: any) { setErr(e.message); }
  }

  const sel: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%' };

  return (
    <div style={S.modal}>
      <div style={{ ...S.modalBox, width: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>{tenant.name}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px', fontSize: 18, lineHeight: 1 }} onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {(['sub', 'flags', 'notes'] as const).map(t => (
            <button key={t} style={{ ...S.btn(tab === t ? 'primary' : 'ghost'), padding: '6px 14px', fontSize: 12 }} onClick={() => setTab(t)}>
              {t === 'sub' ? 'Előfizetés' : t === 'flags' ? 'Feature flag' : 'Jegyzetek'}
            </button>
          ))}
        </div>

        {tab === 'sub' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={S.label}>Csomag</label>
              <select value={plan} onChange={e => setPlan(e.target.value)} style={sel}>
                {['start','standard','pro'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Számlázási ciklus</label>
              <select value={cycle} onChange={e => setCycle(e.target.value)} style={sel}>
                <option value="monthly">Havi</option><option value="yearly">Éves</option>
              </select>
            </div>
            <div><label style={S.label}>Állapot</label>
              <select value={subStatus} onChange={e => setSubStatus(e.target.value)} style={sel}>
                {['trial','active','past_due','grace','read_only','locked','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Trial vége</label>
              <input type="date" value={trialEnd} onChange={e => setTrialEnd(e.target.value)} style={{ ...S.input, width: 'auto' }} />
            </div>
            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={S.btn('primary')} onClick={handleSaveSub} disabled={saving}>{saving ? 'Mentés...' : 'Mentés'}</button>
              <button style={S.btn('secondary')} onClick={onClose}>Mégse</button>
            </div>
          </div>
        )}

        {tab === 'flags' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={S.label}>Feature neve</label>
              <input style={S.input} value={feature} onChange={e => setFeature(e.target.value)} placeholder="pl. warehouse, bank_matching" />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <label style={{ ...S.label, margin: 0 }}>
                <input type="checkbox" checked={featureOn} onChange={e => setFeatureOn(e.target.checked)} style={{ marginRight: 6 }} />
                Engedélyezett
              </label>
            </div>
            {err && <div style={S.err}>{err}</div>}
            <button style={S.btn('primary')} onClick={handleSetFlag} disabled={saving || !feature}>Beállítás</button>
          </div>
        )}

        {tab === 'notes' && (
          <div>
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 14 }}>
              {notes.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Nincs megjegyzés.</div> : notes.map((n: any) => (
                <div key={n.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{n.admin_name} — {fmtDateTime(n.created_at)}</div>
                  <div style={{ fontSize: 13 }}>{n.content}</div>
                </div>
              ))}
            </div>
            <textarea
              value={noteText} onChange={e => setNoteText(e.target.value)}
              placeholder="Új megjegyzés..."
              rows={3}
              style={{ ...S.input, resize: 'vertical' }}
            />
            {err && <div style={S.err}>{err}</div>}
            <button style={{ ...S.btn('primary'), marginTop: 10 }} onClick={handleAddNote} disabled={!noteText.trim()}>Mentés</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── User-support ──────────────────────────────────────────────────────────────

function UsersSection() {
  const [tenantId, setTenantId] = useState('');
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    aGet<{ rows: TenantRow[] }>('/tenants?limit=200').then(r => setTenants(r.rows)).catch(() => {});
  }, []);

  async function loadUsers(tid: string) {
    if (!tid) return;
    setLoading(true);
    try { setUsers(await aGet<any[]>(`/tenants/${tid}/users`)); }
    catch { setUsers([]); }
    finally { setLoading(false); }
  }

  async function setStatus(userId: string, status: 'active' | 'suspended') {
    try {
      await aPost(`/tenants/${tenantId}/users/${userId}/status`, { status });
      setMsg('Állapot frissítve.'); loadUsers(tenantId);
    } catch (e: any) { setMsg(e.message); }
  }

  async function resetPassword(userId: string) {
    try {
      const r = await aPost<{ resetLink: string }>(`/tenants/${tenantId}/users/${userId}/password-reset`, {});
      prompt('Jelszó reset link (egyszer látható):', r.resetLink);
    } catch (e: any) { setMsg(e.message); }
  }

  async function impersonate(userId: string) {
    const reason = prompt('Impersonation indoka (min 5 karakter):');
    if (!reason || reason.length < 5) return;
    try {
      const r = await aPost<{ token: string }>(`/tenants/${tenantId}/impersonate`, { readOnly: true, reason });
      // Store original admin token, set tenant token
      localStorage.setItem('preImpersonateToken', localStorage.getItem('accessToken') || '');
      localStorage.setItem('accessToken', r.token);
      setMsg('Impersonation aktív. Megnyitás: /dashboard');
    } catch (e: any) { setMsg(e.message); }
  }

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Felhasználó support</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Felhasználói állapot, jelszó reset, impersonation</div>

      {msg && <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, color: '#166534', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <select value={tenantId} onChange={e => { setTenantId(e.target.value); loadUsers(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minWidth: 280 }}>
          <option value="">— Válassz tenantot —</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {tenantId && (
        <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Név', 'Email', 'Szerepkör', 'Állapot', 'Utolsó belépés', 'Műveletek'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
              ) : users.map((u: any) => (
                <tr key={u.id}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{u.name || '—'}</td>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}>{u.role}</td>
                  <td style={S.td}><span style={S.badge(u.is_active ? 'green' : 'red')}>{u.is_active ? 'aktív' : 'felfügg.'}</span></td>
                  <td style={S.td}>{fmtDateTime(u.last_login_at)}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {u.is_active
                        ? <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={() => setStatus(u.id, 'suspended')}>Felfüggesztés</button>
                        : <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={() => setStatus(u.id, 'active')}>Aktiválás</button>}
                      <button style={{ ...S.btn('secondary'), fontSize: 12 }} onClick={() => resetPassword(u.id)}>Pw reset</button>
                      <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={() => impersonate(u.id)}>Belépés</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs felhasználó.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Admin fiókok (OWNER only) ─────────────────────────────────────────────────

function AdminsSection({ currentAdmin }: { currentAdmin: AdminUser }) {
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setAdmins(await aGet<AdminRow[]>('/admins')); }
    catch { setMsg('Hozzáférés megtagadva (OWNER szükséges).'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSuspend(a: AdminRow) {
    const reason = prompt('Felfüggesztés oka:');
    if (!reason || reason.length < 5) return;
    try { await aPost(`/admins/${a.id}/suspend`, { reason }); load(); }
    catch (e: any) { setMsg(e.message); }
  }

  async function handleDelete(a: AdminRow) {
    const reason = prompt('Törlés oka (min 5 karakter):');
    if (!reason || reason.length < 5) return;
    if (!confirm(`Biztosan törlöd: ${a.email}?`)) return;
    try { await aDel(`/admins/${a.id}`, { reason }); load(); }
    catch (e: any) { setMsg(e.message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Admin fiókok</div>
        {currentAdmin.role === 'OWNER' && (
          <button style={S.btn('primary')} onClick={() => setShowCreate(true)}>+ Admin létrehozás</button>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Platform admin fiókok kezelése</div>

      {msg && <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Név', 'Email', 'Szerepkör', 'Állapot', 'Utolsó belépés', 'Létrehozva', 'Műveletek'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : admins.map(a => (
              <tr key={a.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{a.name}</td>
                <td style={S.td}>{a.email}</td>
                <td style={S.td}><span style={S.badge(a.role === 'OWNER' ? 'blue' : 'gray')}>{a.role}</span></td>
                <td style={S.td}><span style={S.badge(a.is_active ? 'green' : 'red')}>{a.is_active ? 'aktív' : 'felfügg.'}</span></td>
                <td style={S.td}>{fmtDateTime(a.last_login_at)}</td>
                <td style={S.td}>{fmtDate(a.created_at)}</td>
                <td style={S.td}>
                  {a.id !== currentAdmin.id && currentAdmin.role === 'OWNER' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {a.is_active
                        ? <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={() => handleSuspend(a)}>Felfüggesztés</button>
                        : <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={async () => { try { await aPost(`/admins/${a.id}/unsuspend`, {}); load(); } catch(e:any){setMsg(e.message);} }}>Aktiválás</button>}
                      <button style={{ ...S.btn('danger'), fontSize: 12 }} onClick={() => handleDelete(a)}>Törlés</button>
                    </div>
                  )}
                  {a.id === currentAdmin.id && <span style={{ color: '#94a3b8', fontSize: 12 }}>te</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateAdminModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'ADMINISZTRATOR' | 'SUPPORT' | 'PENZUGYES_ADMIN'>('SUPPORT');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await aPost('/admins', { email, name, role, password });
      onSaved();
    } catch (ex: any) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal}>
      <div style={S.modalBox}>
        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 20 }}>Új admin fiók</div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={S.label}>Email</label><input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
          <div><label style={S.label}>Név</label><input style={S.input} value={name} onChange={e => setName(e.target.value)} required minLength={2} /></div>
          <div>
            <label style={S.label}>Szerepkör</label>
            <select value={role} onChange={e => setRole(e.target.value as any)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%' }}>
              <option value="SUPPORT">SUPPORT</option>
              <option value="ADMINISZTRATOR">ADMINISZTRATOR</option>
              <option value="PENZUGYES_ADMIN">PENZUGYES_ADMIN</option>
            </select>
          </div>
          <div><label style={S.label}>Ideiglenes jelszó (min. 12 karakter)</label><input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={12} /></div>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={S.btn('primary')} disabled={saving}>{saving ? 'Mentés...' : 'Létrehozás'}</button>
            <button type="button" style={S.btn('secondary')} onClick={onClose}>Mégse</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Jóváhagyások ──────────────────────────────────────────────────────────────

function ApprovalsSection({ currentAdmin }: { currentAdmin: AdminUser }) {
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setApprovals(await aGet<ApprovalRow[]>('/approvals?status=pending')); }
    catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleResolve(id: string, approved: boolean) {
    try {
      await aPost(`/approvals/${id}/resolve`, { approved });
      setMsg(approved ? 'Jóváhagyva.' : 'Elutasítva.');
      load();
    } catch (e: any) { setMsg(e.message); }
  }

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Jóváhagyási kérelmek</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Négy-szem elv — OWNER jóváhagyás szükséges</div>

      {msg && <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, color: '#166534', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Típus', 'Leírás', 'Kérelmező', 'Létrehozva', 'Műveletek'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : approvals.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs függő kérelem.</td></tr>
            ) : approvals.map(a => (
              <tr key={a.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{a.action_type}</td>
                <td style={S.td}>{a.description}</td>
                <td style={S.td}>{a.requester_email}</td>
                <td style={S.td}>{fmtDateTime(a.created_at)}</td>
                <td style={S.td}>
                  {currentAdmin.role === 'OWNER' ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...S.btn('primary'), fontSize: 12 }} onClick={() => handleResolve(a.id, true)}>Jóváhagyás</button>
                      <button style={{ ...S.btn('danger'), fontSize: 12 }} onClick={() => handleResolve(a.id, false)}>Elutasítás</button>
                    </div>
                  ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>OWNER jogosultság szükséges</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Kuponok ───────────────────────────────────────────────────────────────────

function CouponsSection() {
  const [coupons, setCoupons] = useState<CouponRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setCoupons(await aGet<CouponRow[]>('/coupons')); }
    catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggle(c: CouponRow) {
    try {
      await aPatch(`/coupons/${c.id}/toggle`, { active: !c.is_active });
      load();
    } catch (e: any) { setMsg(e.message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Kuponok</div>
        <button style={S.btn('primary')} onClick={() => setShowCreate(true)}>+ Kupon létrehozás</button>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Kedvezmény kódok kezelése</div>

      {msg && <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Kód', 'Leírás', 'Kedvezmény', 'Felhasználva', 'Állapot', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : coupons.map(c => (
              <tr key={c.id}>
                <td style={{ ...S.td, fontWeight: 700, letterSpacing: '0.05em' }}>{c.code}</td>
                <td style={S.td}>{c.description || '—'}</td>
                <td style={S.td}>{c.discount_type === 'percent' ? `${c.discount_value}%` : fmtHuf(c.discount_value)}</td>
                <td style={S.td}>{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                <td style={S.td}><span style={S.badge(c.is_active ? 'green' : 'gray')}>{c.is_active ? 'aktív' : 'inaktív'}</span></td>
                <td style={S.td}>
                  <button style={{ ...S.btn('ghost'), fontSize: 12 }} onClick={() => handleToggle(c)}>
                    {c.is_active ? 'Deaktiválás' : 'Aktiválás'}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && coupons.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs kupon.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateCouponModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateCouponModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed_huf'>('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await aPost('/coupons', {
        code: code.toUpperCase(), description: description || undefined,
        discountType, discountValue: parseFloat(discountValue),
        ...(maxUses ? { maxUses: parseInt(maxUses) } : {}),
        ...(validFrom ? { validFrom } : {}),
        ...(validUntil ? { validUntil } : {}),
      });
      onSaved();
    } catch (ex: any) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  const sel: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%' };
  return (
    <div style={S.modal}>
      <div style={S.modalBox}>
        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 20 }}>Új kupon</div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><label style={S.label}>Kód</label><input style={S.input} value={code} onChange={e => setCode(e.target.value.toUpperCase())} required minLength={3} /></div>
          <div><label style={S.label}>Leírás</label><input style={S.input} value={description} onChange={e => setDescription(e.target.value)} /></div>
          <div>
            <label style={S.label}>Kedvezmény típus</label>
            <select value={discountType} onChange={e => setDiscountType(e.target.value as any)} style={sel}>
              <option value="percent">Százalék (%)</option>
              <option value="fixed_huf">Fix összeg (Ft)</option>
            </select>
          </div>
          <div><label style={S.label}>Kedvezmény értéke</label><input style={S.input} type="number" min="0" step="any" value={discountValue} onChange={e => setDiscountValue(e.target.value)} required /></div>
          <div><label style={S.label}>Max felhasználás (opcionális)</label><input style={S.input} type="number" min="1" value={maxUses} onChange={e => setMaxUses(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><label style={S.label}>Érvényes tól</label><input style={S.input} type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} /></div>
            <div style={{ flex: 1 }}><label style={S.label}>Érvényes ig</label><input style={S.input} type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} /></div>
          </div>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button style={S.btn('primary')} disabled={saving}>{saving ? 'Mentés...' : 'Létrehozás'}</button>
            <button type="button" style={S.btn('secondary')} onClick={onClose}>Mégse</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Broadcastok ───────────────────────────────────────────────────────────────

function BroadcastsSection({ currentAdmin }: { currentAdmin: AdminUser }) {
  const [broadcasts, setBroadcasts] = useState<BroadcastRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setBroadcasts(await aGet<BroadcastRow[]>('/broadcasts')); }
    catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSend(id: string) {
    if (!confirm('Elküldöd az összes tenant felhasználónak?')) return;
    try { await aPost(`/broadcasts/${id}/send`, {}); setMsg('Broadcast elküldve.'); load(); }
    catch (e: any) { setMsg(e.message); }
  }

  const statusBadge = (s: string) => s === 'sent' ? 'green' : s === 'pending_approval' ? 'yellow' : 'blue';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Broadcastok</div>
        <button style={S.btn('primary')} onClick={() => setShowCreate(true)}>+ Új broadcast</button>
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Rendszerüzenet küldés (OWNER jóváhagyás szükséges)</div>

      {msg && <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, color: '#166534', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Cím', 'Csatorna', 'Állapot', 'Létrehozva', 'Elküldve', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : broadcasts.map(b => (
              <tr key={b.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{b.title}</td>
                <td style={S.td}>{b.channel}</td>
                <td style={S.td}><span style={S.badge(statusBadge(b.status) as any)}>{b.status}</span></td>
                <td style={S.td}>{fmtDateTime(b.created_at)}</td>
                <td style={S.td}>{fmtDateTime(b.sent_at)}</td>
                <td style={S.td}>
                  {b.status === 'approved' && currentAdmin.role === 'OWNER' && (
                    <button style={{ ...S.btn('primary'), fontSize: 12 }} onClick={() => handleSend(b.id)}>Küldés</button>
                  )}
                  {b.status === 'pending_approval' && <span style={{ fontSize: 12, color: '#94a3b8' }}>Jóváhagyásra vár</span>}
                </td>
              </tr>
            ))}
            {!loading && broadcasts.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs broadcast.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateBroadcastModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateBroadcastModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState<'in_app' | 'email' | 'both'>('in_app');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await aPost('/broadcasts', { title, body, channel });
      onSaved();
    } catch (ex: any) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal}>
      <div style={S.modalBox}>
        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 20 }}>Új broadcast</div>
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={S.label}>Cím</label><input style={S.input} value={title} onChange={e => setTitle(e.target.value)} required minLength={3} /></div>
          <div>
            <label style={S.label}>Üzenet</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} required minLength={10} rows={4}
              style={{ ...S.input, resize: 'vertical' }} />
          </div>
          <div>
            <label style={S.label}>Csatorna</label>
            <select value={channel} onChange={e => setChannel(e.target.value as any)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%' }}>
              <option value="in_app">In-app</option>
              <option value="email">Email</option>
              <option value="both">Mindkettő</option>
            </select>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
            A broadcast OWNER jóváhagyás után küldhető el.
          </div>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={S.btn('primary')} disabled={saving}>{saving ? 'Mentés...' : 'Létrehozás'}</button>
            <button type="button" style={S.btn('secondary')} onClick={onClose}>Mégse</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Leadek ────────────────────────────────────────────────────────────────────

function LeadsSection() {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async (st = status) => {
    setLoading(true);
    try {
      const p = st ? `?status=${st}` : '';
      setLeads(await aGet<LeadRow[]>(`/leads${p}`));
    } catch { }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdateStatus(id: string, newStatus: string) {
    try { await aPatch(`/leads/${id}`, { status: newStatus }); load(); }
    catch (e: any) { setMsg(e.message); }
  }

  const statusColor = (s: string): 'blue' | 'yellow' | 'green' | 'red' | 'gray' => {
    if (s === 'new') return 'blue';
    if (s === 'contacted') return 'yellow';
    if (s === 'converted') return 'green';
    if (s === 'lost') return 'red';
    return 'gray';
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Leadek</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Onboarding érdeklődők nyomon követése</div>

      {msg && <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select value={status} onChange={e => { setStatus(e.target.value); load(e.target.value); }}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">Minden állapot</option>
          {['new','contacted','converted','lost'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Email', 'Név', 'Cég', 'Forrás', 'Állapot', 'Érkezett', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : leads.map(l => (
              <tr key={l.id}>
                <td style={{ ...S.td, fontWeight: 600 }}>{l.email}</td>
                <td style={S.td}>{l.name || '—'}</td>
                <td style={S.td}>{l.company_name || '—'}</td>
                <td style={S.td}>{l.source || '—'}</td>
                <td style={S.td}><span style={S.badge(statusColor(l.status))}>{l.status}</span></td>
                <td style={S.td}>{fmtDate(l.created_at)}</td>
                <td style={S.td}>
                  <select value={l.status} onChange={e => handleUpdateStatus(l.id, e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}>
                    {['new','contacted','converted','lost'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs lead.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Audit napló ────────────────────────────────────────────────────────────────

function AuditSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 50;

  const load = useCallback(async (a = action, p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(p * PAGE) });
      if (a) params.set('action', a);
      const r = await aGet<{ rows: any[]; total: number }>(`/audit?${params}`);
      setRows(r.rows); setTotal(r.total);
    } catch { }
    finally { setLoading(false); }
  }, [action, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Audit napló</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Minden admin művelet naplózva (OWNER only)</div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...S.input, width: 200 }} placeholder="Szűrés műveletre..." value={action}
          onChange={e => { setAction(e.target.value); setPage(0); load(e.target.value, 0); }} />
        <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>{total} rekord</span>
      </div>

      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Időpont', 'Admin', 'Művelet', 'Cél típus', 'IP', 'Részletek'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Betöltés...</td></tr>
            ) : rows.map((r: any) => (
              <tr key={r.id}>
                <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(r.created_at)}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.admin_email || '—'}</td>
                <td style={S.td}><span style={{ fontWeight: 600, fontSize: 12 }}>{r.action}</span></td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.target_type || '—'}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.ip_address || '—'}</td>
                <td style={{ ...S.td, fontSize: 11, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.details ? JSON.stringify(r.details) : '—'}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nincs rekord.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
        <button style={S.btn('secondary')} disabled={page === 0} onClick={() => { setPage(p => p - 1); load(action, page - 1); }}>← Előző</button>
        <span style={{ fontSize: 13, color: '#64748b' }}>{page + 1}. oldal</span>
        <button style={S.btn('secondary')} disabled={(page + 1) * PAGE >= total} onClick={() => { setPage(p => p + 1); load(action, page + 1); }}>Következő →</button>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

const NAV_ITEMS: { id: Tab; label: string; section?: string; ownerOnly?: boolean }[] = [
  { id: 'dashboard', label: 'Dashboard', section: 'Áttekintő' },
  { id: 'tenants', label: 'Tenantok', section: 'Kezelés' },
  { id: 'users', label: 'Felhasználók' },
  { id: 'approvals', label: 'Jóváhagyások' },
  { id: 'finance', label: 'Metrikák', section: 'Pénzügy' },
  { id: 'coupons', label: 'Kuponok' },
  { id: 'broadcasts', label: 'Broadcastok', section: 'Kommunikáció' },
  { id: 'leads', label: 'Leadek' },
  { id: 'admins', label: 'Admin fiókok', section: 'Rendszer', ownerOnly: true },
  { id: 'audit', label: 'Audit napló', ownerOnly: true },
  { id: 'health', label: 'Rendszer állapot' },
];

// ── Fő konzol ─────────────────────────────────────────────────────────────────

function AdminConsoleMain({ adminUser, onLogout }: { adminUser: AdminUser; onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>('dashboard');

  const isOwner = adminUser.role === 'OWNER';

  return (
    <div style={S.wrap}>
      <aside style={S.sidebar}>
        <div style={S.sidebarLogo}>
          Modulum Admin
          <span style={S.sidebarSub}>Platform konzol</span>
        </div>
        <nav style={S.sidebarNav}>
          {NAV_ITEMS.filter(i => !i.ownerOnly || isOwner).map((item, idx) => (
            <div key={item.id}>
              {item.section && (
                <div style={{ ...S.sidebarSection, marginTop: idx > 0 ? 8 : 0 }}>{item.section}</div>
              )}
              <button style={S.sidebarItem(tab === item.id)} onClick={() => setTab(item.id)}>
                {item.label}
              </button>
            </div>
          ))}
        </nav>
        <div style={S.sidebarUser}>
          <div style={{ color: 'rgba(255,255,255,.75)', fontWeight: 600, marginBottom: 2 }}>{adminUser.name}</div>
          <div style={{ fontSize: 11 }}>{adminUser.email}</div>
          <div style={{ ...S.badge('blue' as any), marginTop: 6, fontSize: 10 }}>{adminUser.role}</div>
          <button
            style={{ display: 'block', marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onClick={onLogout}
          >
            Kijelentkezés
          </button>
        </div>
      </aside>

      <main style={S.main}>
        {tab === 'dashboard' && <DashboardSection />}
        {tab === 'tenants' && <TenantSection />}
        {tab === 'users' && <UsersSection />}
        {tab === 'admins' && <AdminsSection currentAdmin={adminUser} />}
        {tab === 'finance' && <DashboardSection />}
        {tab === 'coupons' && <CouponsSection />}
        {tab === 'approvals' && <ApprovalsSection currentAdmin={adminUser} />}
        {tab === 'broadcasts' && <BroadcastsSection currentAdmin={adminUser} />}
        {tab === 'leads' && <LeadsSection />}
        {tab === 'audit' && <AuditSection />}
        {tab === 'health' && <HealthSection />}
      </main>
    </div>
  );
}

function HealthSection() {
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aGet<any>('/system/health').then(setHealth).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Betöltés...</div>;
  if (!health) return <div style={{ padding: 40, color: '#ef4444' }}>Hozzáférés megtagadva.</div>;

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Rendszer állapot</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>Backend szolgáltatások</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 14 }}>
        {Object.entries(health).map(([k, v]) => (
          <div key={k} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', flexShrink: 0, background: v === 'ok' || v === true ? '#16a34a' : '#ef4444' }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{k}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{String(v)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── App belépőpont ─────────────────────────────────────────────────────────────

export default function AdminConsoleApp() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(() => {
    try { return JSON.parse(localStorage.getItem('adminUser') || 'null'); } catch { return null; }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('adminToken'));

  // Ha van tárolt felhasználó + token, validáljuk
  useEffect(() => {
    if (adminUser && token) {
      aGet<AdminUser>('/auth/me')
        .then(u => {
          setAdminUser(u);
          localStorage.setItem('adminUser', JSON.stringify(u));
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          setAdminUser(null);
        });
    }
  }, []);

  function handleLogin(user: AdminUser, newToken: string) {
    setToken(newToken);
    setAdminUser(user);
  }

  function handleLogout() {
    const t = localStorage.getItem('adminToken');
    if (t) { aPost('/auth/logout', {}).catch(() => {}); }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setAdminUser(null);
  }

  function handlePasswordChanged() {
    const u = JSON.parse(localStorage.getItem('adminUser') || '{}');
    u.must_change_password = false;
    localStorage.setItem('adminUser', JSON.stringify(u));
    setAdminUser(u);
  }

  if (!adminUser || !token) {
    return <AdminLoginPage onLogin={handleLogin} />;
  }

  if (adminUser.must_change_password) {
    return <ChangePasswordPage onDone={handlePasswordChanged} />;
  }

  return <AdminConsoleMain adminUser={adminUser} onLogout={handleLogout} />;
}
